import React from 'react';
import { useReso } from '../contexts/ResoContext';
import { useTenant } from '../contexts/TenantContext';

interface useQueryMemberLicensesProps {
  memberKey: string | undefined;
  stateOrProvince: string | undefined;
  setLicenseNumber: React.Dispatch<React.SetStateAction<string | undefined>>;
  setLicenseExpiry: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
}

export const useQueryMemberLicenses = () => {
  const { executeQuery } = useReso();
  const { getConfigValue } = useTenant();

  const isLicensesEnabledForTenant = getConfigValue<boolean>('ENABLE_LICENSES');

  const queryMemberLicenses = async ({
    memberKey,
    stateOrProvince,
    setLicenseNumber,
    setLicenseExpiry,
    setIsLoading,
  }: useQueryMemberLicensesProps) => {
    try {
      // tenant has opted out of the license resource
      if (!isLicensesEnabledForTenant) {
        return;
      }

      // member is missing or not an agent, skip getting license number
      if (!memberKey) {
        return;
      }

      // something is wrong with listing context
      if (!stateOrProvince) {
        return;
      }

      const result = await executeQuery<any>('SysPartyLicense', {
        queryParams: {
          $filter: `SysPartyLicensePartyKey eq '${memberKey}' and SysPartyLicenseState eq '${stateOrProvince}'`,
          $format: 'json',
        },
      });

      const firstMatchingLicense = result?.value?.[0];

      // no licenses found for member in current state, or something wrong with the data
      if (firstMatchingLicense) {
        setLicenseNumber(firstMatchingLicense?.SysPartyLicenseNumber);
        setLicenseExpiry(firstMatchingLicense?.SysPartyLicenseExpirationDate);
      } else {
        setLicenseNumber(undefined);
        setLicenseExpiry(undefined);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    queryMemberLicenses,
  };
};
